import "./App.css";
import "react-date-range/dist/styles.css"; // style for react-date-range
import "react-date-range/dist/theme/default.css"; // style for react-date-range
import React, { useState, useEffect, useRef, useContext } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import {
  UJValidateRegistrationForm,
  UJValidateRegistrationFormV2,
  copilotAgentSupplyCore,
  getSidebarMenu,
  getSidebarMenuV2,
  onBoardingRequest,
  onboardingRequestV2,
  getSupplyCoreAuthBaseUrl,
} from "./services/api.service";
import "react-toastify/dist/ReactToastify.css";
import { CircularProgress } from "@mui/material";
import "../node_modules/flag-icons/css/flag-icons.min.css";
import { appContext } from "./states/context/appContext";

import OnboardingComponent from "./components/onboarding/OnboardingComponent";
import { ToastContainer, toast } from "react-toastify";
import RoutesComponent from "./pages/RoutesComponent";
import AuthErrorPage from "./pages/AuthErrorPage";
import ConsentSuccessPage from "./pages/ConsentSuccessPage";
import Automations from "./components/userOnboarding/Automations";
import Onboarding from "./components/userOnboarding/Onboarding";
import OnboardingStatus from "./components/userOnboarding/OnboardingStatus.jsx";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "./components/userOnboarding/common/UserOnboardingTheme";
import { appTheme } from "./theme";
import { useSession } from "./hooks/useSession";
import { SessionContext } from "./contexts/SessionContext";

const App = () => {
  const globalContext = useContext(appContext);
  const [loading, setLoading] = useState(true);
  const [getTemplateApiData, setGetTemplateApiData] = useState({
    meta: "",
    data: "",
  });

  const urlParams = new URLSearchParams(window.location.search);
  const sandboxSlug = urlParams.get("sandbox");
  if (sandboxSlug) localStorage.setItem("sc-sandbox", sandboxSlug);

  const [isUserOnboarding, setIsUserOnboarding] = useState(false);
  const [isUserOnAuthError, setIsUserOnAuthError] = useState(false);

  const pathName = window.location.pathname;
  const isSpecialPath = pathName === "/error" || pathName === "/admin-grant/error" || pathName === "/admin-grant/success" || pathName.includes("/onboarding");

  const { session, isLoading: sessionLoading, isError: sessionError } = useSession();

  // Track whether the app bootstrap has already been triggered
  const bootstrapDoneRef = useRef(false);

  useEffect(() => {
    if (pathName === "/error" || pathName === "/admin-grant/error" || pathName === "/admin-grant/success") {
      setIsUserOnAuthError(true);
      return;
    }
    if (pathName.includes("/onboarding")) {
      setIsUserOnboarding(true);
    }
  }, []);

  // Redirect to Auth0 login when session fetch fails
  useEffect(() => {
    if (!isSpecialPath && !sessionLoading && sessionError) {
      const authBaseUrl = getSupplyCoreAuthBaseUrl();
      const loginUrl = `${authBaseUrl}/auth/login?return_to=${encodeURIComponent(window.location.href)}`;
      window.location.assign(loginUrl);
    }
  }, [sessionLoading, sessionError]);

  // Bootstrap the app once session is available
  useEffect(() => {
    if (!isSpecialPath && session && !sessionLoading && !bootstrapDoneRef.current) {
      bootstrapDoneRef.current = true;
      try {
        extractRegionFromHostname();
        copilotAgentRegisterNewUser();
      } catch (e) {
        setLoading(false);
        console.error('[App] Bootstrap error:', e);
      }
    }
  }, [session, sessionLoading]);

  const extractRegionFromHostname = () => {
    const hostname = window.location.hostname;

    if (hostname === "localhost") {
      const defaultRegion = "us";
      globalContext.setRegion({
        ...globalContext.region,
        selectedRegion: defaultRegion
      });
      sessionStorage.setItem("kavida_region", defaultRegion);
      return;
    }

    const regionPattern = /^([a-z]+)(?:-preprod)?-platform\.kavida\.ai$/i;
    const match = hostname.match(regionPattern);

    if (match && match[1]) {
      const regionFromUrl = match[1].toLowerCase();
      const regionMapping = { 'eu': 'eu', 'us': 'us', 'ap': 'ap' };
      const mappedRegion = regionMapping[regionFromUrl] || regionFromUrl;
      globalContext.setRegion({
        ...globalContext.region,
        selectedRegion: mappedRegion
      });
      sessionStorage.setItem("kavida_region", mappedRegion);
    } else {
      const defaultRegion = "us";
      globalContext.setRegion({
        ...globalContext.region,
        selectedRegion: defaultRegion
      });
      sessionStorage.setItem("kavida_region", defaultRegion);
    }
  };

  const sendTrackerInformationWrapper = async (name, email) => {
    try {
      await getSidebarMenuV2({
        api_name: "global/platform-tracker",
        data: {
          name: name,
          email: email,
          action: "login",
          payload: {
            url: window.location.href
          }
        },
      });
    } catch (error) {
      console.error(`error`, error);
    }
  };

  const getData = async () => {
    try {
      setLoading(true);
      const payLoad = {
        api_name: "global/template",
      };
      const res = await getSidebarMenuV2(payLoad, "GET");
      const { meta, data } = res || {};
      if (!meta || meta.status !== 200) {
        setLoading(false);
        return;
      }
      globalContext.setUserRole(data[0].users.role);
      if (data[0]?.users?.user_workflow !== undefined) {
        globalContext.setUserWorkflow(data[0].users.user_workflow);
      }
      if (data[0]?.users?.data_push_types_enabled !== undefined) {
        globalContext.setDataPushTypesEnabled(data[0].users.data_push_types_enabled);
      }
      if (data[0]?.users?.erp_sync !== undefined) {
        globalContext.setErpSync(data[0].users.erp_sync);
      }
      if (data[0]?.users?.erp_supports_splits !== undefined) {
        globalContext.setErpSupportsSplits(data[0].users.erp_supports_splits);
      }
      setGetTemplateApiData({
        meta: meta,
        data: data,
      });
      const email = data[0].users.email;
      const name = data[0].users.name;
      sendTrackerInformationWrapper(name, email);
      setLoading(false);
      globalContext.setUserEmail(data[0].users.email);
      if (data[0].users.first_time_login) {
        globalContext.setIsFirstTimeLogin(true);
      }
    } catch (error) {
      console.error('[App] getData error:', error);
      setLoading(false);
    }
  };

  const copilotAgentRegisterNewUser = async () => {
    try {
      setLoading(true);
      const name = session?.user?.name || '';
      const email = session?.user?.email || '';
      const companyId = session?.tenant?.client_id || '';
      const payLoad = {
        api_name: "copilot-agent/register-sso-user",
        data: {
          user_name: name,
          user_email: email,
          user_phone: "",
          company_name: "",
          company_id: companyId,
          // token field removed — copilot API uses cookie-session auth
        },
      };
      const { meta, data } = await copilotAgentSupplyCore(payLoad);
      if (meta.message === "Success") {
        const {
          redirect_url,
          agent_first_time_session,
          trigger_po_onboarding,
        } = data;

        if (agent_first_time_session) {
          localStorage.setItem("agent_first_time_login", "true");
        }
        if (redirect_url) {
          document.cookie = `trigger_po_onboarding=${
            trigger_po_onboarding ? "true" : ""
          }; path=/;`;
          localStorage.setItem(
            "trigger_po_onboarding",
            trigger_po_onboarding ? "true" : ""
          );
          window.location.replace(redirect_url);
        } else {
          const trigger_po_onboardingCookie = getCookie("trigger_po_onboarding");
          if (
            (localStorage.getItem("trigger_po_onboarding") ||
              trigger_po_onboardingCookie) &&
            localStorage.getItem("agent_first_time_login")
          ) {
            triggerPOOnboarding();
            globalContext.setIsAgentFirstTimeLogin(true);
            globalContext.setIsCopilotOnboarding(true);
            localStorage.removeItem("trigger_po_onboarding");
            document.cookie =
              "trigger_po_onboarding=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
            localStorage.removeItem("agent_first_time_login");
          } else {
            if (!agent_first_time_session && !localStorage.getItem("agent_first_time_login")) {
              localStorage.removeItem("trigger_po_onboarding");
              localStorage.removeItem("agent_first_time_login");
              document.cookie =
                "trigger_po_onboarding=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
            }
            getData();
          }
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error('[App] copilotAgentRegisterNewUser error:', error);
    }
  };

  function getCookie(name) {
    let cookieArray = document.cookie.split(";");
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i].trim();
      if (cookie.indexOf(name + "=") === 0) {
        return cookie.substring(name.length + 1);
      }
    }
    return null;
  }

  const triggerPOOnboarding = async () => {
    try {
      setLoading(true);
      const payLoad = {
        api_name: "copilot-agent/trigger-po-onboarding",
        data: {},
        // token field removed — uses cookie-session auth
      };
      const { meta, data } = await copilotAgentSupplyCore(payLoad);
      if (meta.message === "Success") {
        getData();
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  if (isUserOnAuthError) {
    return (
      <ThemeProvider theme={appTheme}>
        <BrowserRouter>
          <Routes>
            <Route path="/error" element={<AuthErrorPage />} />
            <Route path="/admin-grant/error" element={<AuthErrorPage />} />
            <Route path="/admin-grant/success" element={<ConsentSuccessPage />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    );
  }

  if (isUserOnboarding) {
    return (
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <Routes>
            <Route
              path="/onboarding/automation"
              element={<Automations />}
            />
            <Route
              path="/onboarding/retry"
              element={<OnboardingStatus isCompletedOnboarding={false} />}
            />
            <Route
              path="/onboarding/completed"
              element={<OnboardingStatus isCompletedOnboarding={true} />}
            />
            <Route
              path="/onboarding/:step"
              element={<Onboarding />}
            />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    );
  }

  return (
    <SessionContext.Provider value={session}>
      <ThemeProvider theme={appTheme}>
        <ToastContainer style={{ width: 'fit-content' }} />
        {session && !loading ? (
          !globalContext.isFirstTimeLogin ? (
            <BrowserRouter>
              <Routes>
                <Route
                  path="/*"
                  element={
                    <RoutesComponent
                      getTemplateApiData={getTemplateApiData}
                    />
                  }
                />
              </Routes>
            </BrowserRouter>
          ) : (
            <OnboardingComponent />
          )
        ) : (
          <div className="fixed-loading-container">
            <CircularProgress aria-label="Loading application" />
          </div>
        )}
      </ThemeProvider>
    </SessionContext.Provider>
  );
};

export default App;
